import type React from 'react';

import type { ModalApi, ModalOptions, ModalProps } from './types/modal.types';

import { ActivateCardComponent } from './components/ActivateCardComponent';
import { CreateAdminModalComponent } from './components/admins/CreateAdminModalComponent';
import { EditAdminScopeModalComponent } from './components/admins/EditAdminScopeModalComponent';
import { ApplyDelinquencyComponent } from './components/ApplyDelinquencyComponent';
import { BlockUnblockCardActionComponent } from './components/BlockUnblockCard';
import { CloseLoanComponent } from './components/CloseLoanComponent';
import { AddFeeTransactionComponent } from './components/dev/AddFeeTransactionComponent';
import { DisbursementComponent } from './components/dev/DisbursementComponent';
import { LedgerTransactionForm } from './components/dev/LedgerTransactionForm';
import { PayStatementComponent } from './components/dev/PayStatementComponent';
import { UpdateObanJobModal } from './components/dev/UpdateObanJobModal';
import { UserTwispTimeManagement } from './components/dev/UserTwispTimeManagement';
import { GenerateUserStatementComponent } from './components/GenerateUserStatementComponent';
import { GetCardStatusComponent } from './components/GetCardStatusComponent';
import { MarkPhysicalCardActivatedComponent } from './components/MarkPhysicalCardActivatedComponent';
import { AddLoanMemberModalComponent } from './components/onboarding/AddLoanMemberModalComponent';
import { CreateUserModalComponent } from './components/onboarding/CreateUserModalComponent';
import { OpenAccountModalComponent } from './components/onboarding/OpenAccountModalComponent';
import { UserQuickLookModalComponent } from './components/onboarding/UserQuickLookModalComponent';
import { CreatePartnerComponent } from './components/partners/CreatePartnerComponent';
import { PostExternalRepaymentComponent } from './components/PostExternalRepaymentComponent';
import { CreatePrimeRateComponent } from './components/prime-rates/CreatePrimeRateComponent';
import { EditPrimeRateComponent } from './components/prime-rates/EditPrimeRateComponent';
import { ProcessLateFeeComponent } from './components/ProcessLateFeeComponent';
import { ProcessStatementDueDateComponent } from './components/ProcessStatementDueDateComponent';
import { ActivateProgramComponent } from './components/programs/ActivateProgramComponent';
import { CreateProgramComponent } from './components/programs/CreateProgramComponent';
import { EditProgramComponent } from './components/programs/EditProgramComponent';
import { RemoveProgramComponent } from './components/programs/RemoveProgramComponent';
import { RestoreProgramTwispComponent } from './components/programs/RestoreProgramTwispComponent';
import { ReplaceCardActionComponent } from './components/ReplaceCardActionComponent';
import { ReturnAchRequestComponent } from './components/ReturnAchRequestComponent';
import { ToggleFriendsAndFamilyComponent } from './components/ToggleFriendsAndFamilyComponent';
import { UpdateAprMarginComponent } from './components/UpdateAprMarginComponent';
import { UpdateAutoPayOptionsComponent } from './components/UpdateAutoPayOptionsComponent';
import { UpdateCreditLimitComponent } from './components/UpdateCreditLimitComponent';

const programManagementModals = {
  'create-program': {
    component: CreateProgramComponent,
    options: {
      title: 'Create Program',
      description: 'Create a new program',
    },
  },
  'edit-program': {
    component: EditProgramComponent,
    options: {
      title: 'Edit Program',
      description: 'Edit program details',
    },
  },
  'activate-program': {
    component: ActivateProgramComponent,
    options: {
      title: 'Activate Program',
      description: 'Reactivate a deactivated program',
    },
  },
  'remove-program': {
    component: RemoveProgramComponent,
    options: {
      title: 'Deactivate Program',
      description: 'Deactivate program permanently',
    },
  },
  'restore-program-twisp': {
    component: RestoreProgramTwispComponent,
    options: {
      title: 'Restore Twisp Resources',
      description: 'Restore missing program-level Twisp resources',
    },
  },
};

const partnerModals = {
  'create-partner': {
    component: CreatePartnerComponent,
    options: {
      title: 'Provision Partner',
      description: 'Create a partner with its Auth0 machine client',
    },
  },
};

const primeRateModals = {
  'create-prime-rate': {
    component: CreatePrimeRateComponent,
    options: {
      title: 'Create Prime Rate',
      description: 'Add a new prime rate',
    },
  },
  'edit-prime-rate': {
    component: EditPrimeRateComponent,
    options: {
      title: 'Edit Prime Rate',
      description: 'Update an existing prime rate',
    },
  },
};

const modalRegistry = {
  ...programManagementModals,
  ...partnerModals,
  ...primeRateModals,
  'onboard-loan': {
    component: OpenAccountModalComponent,
    options: {
      title: 'Open Account',
      description: 'Open an account and board its loan for one or two applicants',
      className: 'max-w-6xl',
    },
  },
  'add-loan-member': {
    component: AddLoanMemberModalComponent,
    options: {
      title: 'Add Loan Member',
      description: 'Add another person to this loan',
      className: 'max-w-3xl',
    },
  },
  'create-user': {
    component: CreateUserModalComponent,
    options: {
      title: 'Create Person',
      description: 'Create a customer who is on no loan yet',
      className: 'max-w-4xl',
    },
  },
  'user-quick-look': {
    component: UserQuickLookModalComponent,
    options: {
      title: 'Quick Look',
      description: 'Confirm this is the right person',
      className: 'max-w-xl',
    },
  },
  'create-admin': {
    component: CreateAdminModalComponent,
    options: {
      title: 'Create Admin',
      description: 'Create a partner admin or support agent',
      className: 'max-w-lg',
    },
  },
  'edit-admin-scope': {
    component: EditAdminScopeModalComponent,
    options: {
      title: 'Edit Admin Scope',
      description: 'The whole partner, or one of its programs',
      className: 'max-w-lg',
    },
  },
  'activate-card': {
    component: ActivateCardComponent,
    options: {
      title: 'Activate Card',
      description: 'Activate a user card',
    },
  },
  'add-fee-transaction': {
    component: AddFeeTransactionComponent,
    options: {
      title: 'Add Fee Transaction',
      description: 'Dev/Test Environment Only - Add fee transaction',
    },
  },
  'block-unblock-card': {
    component: BlockUnblockCardActionComponent,
    options: {
      title: 'Block/Unblock Card',
      description: 'Block or unblock a user card',
    },
  },
  'process-statement-due-date': {
    component: ProcessStatementDueDateComponent,
    options: {
      title: 'Process Statement Due Date',
      description: 'Process a user statement due date',
    },
  },
  'process-late-fee': {
    component: ProcessLateFeeComponent,
    options: {
      title: 'Process Late Fee',
      description: 'Process a late fee for a user',
    },
  },
  'return-ach-request': {
    component: ReturnAchRequestComponent,
    options: {
      title: 'Return ACH Request',
      description: 'Preview and submit ACH return actions',
      className: 'max-w-3xl',
    },
  },
  'post-external-repayment': {
    component: PostExternalRepaymentComponent,
    options: {
      title: 'Post External Repayment',
      description: 'Post a repayment that settled outside Strado ACH rails',
    },
  },
  'close-statement-cycle': {
    component: GenerateUserStatementComponent,
    options: {
      title: 'Close Statement Cycle',
      description: 'Close a user statement cycle',
    },
  },
  'close-loan': {
    component: CloseLoanComponent,
    options: {
      title: 'Close Loan',
      description: 'Permanently close a paid-off loan',
      dismissible: false,
    },
  },
  'get-card-status': {
    component: GetCardStatusComponent,
    options: {
      title: 'Get Card Status',
      description: 'Check current card status',
    },
  },
  'mark-physical-card-activated': {
    component: MarkPhysicalCardActivatedComponent,
    options: {
      title: 'Mark Physical Card Activated',
      description: 'Mark physical card as activated',
    },
  },
  'pay-statement': {
    component: PayStatementComponent,
    options: {
      title: 'Pay Statement',
      description: 'Dev/Test Environment Only - Pay statement',
    },
  },
  'replace-card': {
    component: ReplaceCardActionComponent,
    options: {
      title: 'Replace Card',
      description: 'Replace a user card',
    },
  },
  'update-auto-pay-options': {
    component: UpdateAutoPayOptionsComponent,
    options: {
      title: 'Update Auto Pay Options',
      description: 'Configure auto pay settings',
    },
  },
  'update-apr-margin': {
    component: UpdateAprMarginComponent,
    options: {
      title: 'Update APR Margin',
      description: 'Update the APR margin for this user',
    },
  },
  'update-credit-limit': {
    component: UpdateCreditLimitComponent,
    options: {
      title: 'Update Credit Limit',
      description: 'Modify user credit limit',
    },
  },
  'toggle-friends-and-family': {
    component: ToggleFriendsAndFamilyComponent,
    options: {
      title: 'Update Friends & Family',
      description: 'Mark or remove a user from Friends & Family',
    },
  },
  'toggle-delinquency': {
    component: ApplyDelinquencyComponent,
    options: {
      title: 'Update Delinquency Status',
      description: 'Apply or cure delinquency for this user',
    },
  },
  'ledger-transaction': {
    component: LedgerTransactionForm,
    options: {
      title: 'Ledger Transaction',
      description: 'Dev/Test Environment Only - Create ledger transaction',
    },
  },
  'user-twisp-time-management': {
    component: UserTwispTimeManagement,
    options: {
      title: 'User Twisp Time Management',
      description: 'Dev/Test Environment Only - User time server controls',
    },
  },
  'update-oban-job': {
    component: UpdateObanJobModal,
    options: {
      title: 'Update Oban Job',
      description: 'Dev/Test Environment Only - Update job fields',
      className: 'max-w-3xl',
    },
  },
  disbursement: {
    component: DisbursementComponent,
    options: {
      title: 'Create Disbursement',
      description: 'Dev/Test Environment Only - Disburse funds for a loan',
    },
  },
};

type ModalRegistry = typeof modalRegistry;

export type ModalKey = keyof ModalRegistry;

type ModalPropsOf<K extends ModalKey> = React.ComponentProps<ModalRegistry[K]['component']>;

export type ModalPayloadMap = { [K in ModalKey]: ModalPropsOf<K>['payload'] };

export type ModalResultMap = {
  [K in ModalKey]: ModalPropsOf<K>['api'] extends ModalApi<infer Result> ? Result : never;
};

export type ModalDefinitions = {
  [K in ModalKey]: {
    component: React.FC<ModalProps<ModalPayloadMap[K], ModalResultMap[K]>>;
    options: ModalOptions;
  };
};

export const modalDefinitions: ModalDefinitions = modalRegistry;

type ConstantCase<Key extends string> = Key extends `${infer Head}-${infer Tail}`
  ? `${Uppercase<Head>}_${ConstantCase<Tail>}`
  : Uppercase<Key>;

// SAFETY: each entry's name is built by the same upper-case and dash-to-underscore transform ConstantCase spells out.
export const MODALS = Object.fromEntries(
  Object.keys(modalRegistry).map((key) => [key.toUpperCase().replace(/-/g, '_'), key]),
) as { readonly [K in ModalKey as ConstantCase<K>]: K };
